import React from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import {
  createSignatureField,
  DEFAULT_SIGNATURE_FIELD_LABEL,
  FormValues,
} from '../../ManageSignaturePageModal.meta';

interface Props {
  control: Control<FormValues>;
  errorMessage?: string;
}

export const SignatureFieldsList = ({ control, errorMessage }: Props) => {
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'signatureFields',
    keyName: 'key',
  });

  const addField = () => {
    append(createSignatureField(fields.length));
  };

  const removeField = (index: number) => {
    if (fields.length <= 1) {
      return;
    }

    const remainingFields = fields
      .filter((_, ind) => ind !== index)
      .map((field, ind) => ({
        id: `sig-${ind + 1}`,
        label: DEFAULT_SIGNATURE_FIELD_LABEL,
      }));

    replace(remainingFields);
  };

  return (
    <Box>
      <Text variant="formLabel" mb="8">
        Signature
      </Text>

      {fields.map((field, index) => (
        <Box key={field.key} display="flex" alignItems="flex-start" gridGap="8" mb="12">
          <Box flex={1}>
            {/* The label input is always disabled; it exists only to indicate how many signature fields to show. */}
            <Input
              aria-label={`Signature field ${index + 1}`}
              placeholder={field.label ?? DEFAULT_SIGNATURE_FIELD_LABEL}
              disabled
              value=""
            />
          </Box>

          {fields.length > 1 && (
            <Button
              variant="subtleInactive"
              size="small"
              aria-label={`Remove signature field ${index + 1}`}
              onClick={() => removeField(index)}
              mt="4"
            >
              <Icon className="far fa-trash-alt" />
            </Button>
          )}
        </Box>
      ))}

      <Button variant="subtleActive" size="small" leftIcon="far fa-plus" onClick={addField}>
        Add Signature Field
      </Button>

      {errorMessage && (
        <HelperText variant="error" mt="4">
          {errorMessage}
        </HelperText>
      )}
    </Box>
  );
};
